import { FunctionComponent, useCallback, useMemo } from 'react';
import { Card as CardForm } from './Card';
import { Klarna } from './Klarna';
import { Invoice } from './Invoice';
import { PayPal } from './PayPal';
import { Manual } from './Manual';
import { Invoice as InvoiceDetails, PaymentMethod } from '@shared/types';
import styles from './styles.module.scss';
import { useCheckoutContext } from '../CheckoutContext';
import { RadioGroup } from '../../RadioGroup';
import { useIam } from '../../../services/IAm/hooks';
type Option = { label: string; value: PaymentMethod; PaymentForm: FunctionComponent };

const cardPaymentOption: Option = {
    label: 'Card / Apple Pay / Google Pay',
    value: 'card',
    PaymentForm: () => {
        const { checkoutState, submitResponse } = useCheckoutContext();
        const onPaymentSuccess = useCallback(() => {
            submitResponse({
                actionType: 'payment was successful',
                payload: {},
            });
        }, [submitResponse]);
        const { iam, iamReady } = useIam();
        const billingDetails = useMemo(() => {
            return iamReady && iam.authenticated
                ? {
                      name: `${iam.firstName} ${iam.lastName}`,
                      email: iam.email,
                  }
                : undefined;
        }, [iam, iamReady]);
        if (
            checkoutState.flow !== 'booking' &&
            checkoutState.flow !== 'order' &&
            checkoutState.flow !== 'request'
        ) {
            return null;
        }
        if (checkoutState.stepState.payment.state?.paymentMethod !== 'card') {
            return null;
        }
        if (!billingDetails) {
            return null;
        }
        return (
            <CardForm
                onPaymentSuccess={onPaymentSuccess}
                stripeClientSecret={checkoutState.stepState.payment.state.stripeClientSecret}
                stripeAccountId={checkoutState.stepState.payment.state.stripeAccountId}
                billingDetails={billingDetails}
            />
        );
    },
};

const paypalPaymentOption: Option = {
    label: 'PayPal',
    value: 'paypal',
    PaymentForm: () => {
        const { checkoutState, submitResponse } = useCheckoutContext();
        const onPaymentSuccess = useCallback(() => {
            submitResponse({
                actionType: 'payment was successful',
                payload: {},
            });
        }, [submitResponse]);
        if (
            checkoutState.flow !== 'booking' &&
            checkoutState.flow !== 'order' &&
            checkoutState.flow !== 'request'
        ) {
            return null;
        }
        if (checkoutState.stepState.payment.state?.paymentMethod !== 'paypal') {
            return null;
        }
        return (
            <PayPal
                onPaymentSuccess={onPaymentSuccess}
                paypalOrderId={checkoutState.stepState.payment.state.paypalOrderId}
            />
        );
    },
};

const klarnaPaymentOption: Option = {
    label: 'Klarna',
    value: 'klarna',
    PaymentForm: () => {
        const { checkoutState, submitResponse } = useCheckoutContext();
        const onPaymentSuccess = useCallback(() => {
            submitResponse({
                actionType: 'payment was successful',
                payload: {},
            });
        }, [submitResponse]);
        if (checkoutState.flow !== 'booking' && checkoutState.flow !== 'order') {
            return null;
        }
        if (checkoutState.stepState.payment.state?.paymentMethod !== 'klarna') {
            return null;
        }
        return (
            <Klarna
                onPaymentSuccess={onPaymentSuccess}
                stripeClientSecret={checkoutState.stepState.payment.state.stripeClientSecret}
                stripeAccountId={checkoutState.stepState.payment.state.stripeAccountId}
            />
        );
    },
};

const invoicePaymentOption = {
    label: 'Invoice',
    value: 'invoice',
    PaymentForm: () => {
        const { checkoutState, submitResponse } = useCheckoutContext();

        const onInvoiceSubmitted = useCallback(
            (invoiceDetails: InvoiceDetails) => {
                submitResponse({
                    actionType: 'invoice details added',
                    payload: invoiceDetails,
                });
            },
            [submitResponse]
        );
        if (
            checkoutState.flow !== 'booking' &&
            checkoutState.flow !== 'order' &&
            checkoutState.flow !== 'request'
        ) {
            return null;
        }
        if (checkoutState.stepState.payment.state?.paymentMethod !== 'invoice') {
            return null;
        }
        return (
            <Invoice
                onSubmit={onInvoiceSubmitted}
                initialInvoiceDetails={checkoutState.stepState.payment.state.invoiceDetails}
            />
        );
    },
};

const manualPaymentOption = {
    label: 'In-person Payment',
    value: 'manual',
    PaymentForm: () => {
        const { submitResponse, paymentOptions } = useCheckoutContext();

        const onConfirmed = useCallback(() => {
            submitResponse({
                actionType: 'manual payment confirmed',
                payload: {},
            });
        }, [submitResponse]);

        return <Manual message={paymentOptions.manual.message} onConfirmed={onConfirmed} />;
    },
};

export const PaymentForm: FunctionComponent = () => {
    const { checkoutState, submitResponse, paymentOptions, isLoading } = useCheckoutContext();

    const onBackClick = useCallback(() => {
        return submitResponse({ actionType: 'go back', payload: {} });
    }, [submitResponse]);

    const manualPaymentOptionLabel = useMemo(() => {
        return paymentOptions.manual.label || 'In-person Payment';
    }, [paymentOptions.manual.label]);

    const manualPaymentOptionWithLabel = useMemo(() => {
        return { ...manualPaymentOption, label: manualPaymentOptionLabel };
    }, [manualPaymentOptionLabel]);

    const selectableOptions = useMemo((): Array<Option> => {
        return [
            paymentOptions.card.enabled ? cardPaymentOption : null,
            paymentOptions.paypal.enabled ? paypalPaymentOption : null,
            paymentOptions.klarna.enabled ? klarnaPaymentOption : null,
            paymentOptions.invoice.enabled ? invoicePaymentOption : null,
            paymentOptions.manual.enabled ? manualPaymentOptionWithLabel : null,
        ].filter((option): option is Option => {
            return option !== null;
        });
    }, [paymentOptions]);

    const selected = useMemo(() => {
        if (
            checkoutState.flow !== 'booking' &&
            checkoutState.flow !== 'order' &&
            checkoutState.flow !== 'request'
        ) {
            return null;
        }
        return checkoutState.stepState.payment.state?.paymentMethod;
    }, [checkoutState]);

    const selectedOption = useMemo((): Option | undefined => {
        return selectableOptions.find((option) => option.value === selected);
    }, [selectableOptions, selected]);

    const onMethodOptionSelected = useCallback(
        (selectedOption: Option) => {
            if (isLoading) {
                return;
            }
            const method = selectedOption.value;
            submitResponse({
                actionType: 'payment method selected',
                payload: {
                    paymentMethod: method,
                },
            });
        },
        [submitResponse, isLoading]
    );

    return (
        <RadioGroup
            className={styles.optionsContainer}
            value={selectedOption}
            options={selectableOptions}
            onChange={onMethodOptionSelected}
        >
            {(option, isSelected) => {
                return (
                    <div>
                        <div className={styles.optionHeading}>
                            <div className={styles.radio}>
                                <div className={styles.radioChecked}></div>
                            </div>
                            <span>{option.label}</span>
                        </div>
                        {isSelected && option.PaymentForm ? (
                            <div className={styles.selectedOptionContainer}>
                                <option.PaymentForm />
                            </div>
                        ) : null}
                    </div>
                );
            }}
        </RadioGroup>
    );
};
